import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  Colors,
} from 'chart.js';
import { Pie } from 'react-chartjs-2';
import _ from 'lodash';
import React, { useRef } from 'react';
import { useDispatch } from 'react-redux';

import { storeDashboardAnalysis } from '../service/widget.service';
import { getRandomColor } from '../../Dashboard/helpers/color.helper';
import { setChartClickResponse } from '../../../../redux/slices/page-header.slice';

ChartJS.register(ArcElement, Tooltip, Legend, Colors);

const PieChartComponent = ({
  cubeResponse,
  annotation,
  dashboardDetails,
  dashboardLogDetails,
}) => {
  if (!cubeResponse || !cubeResponse.length) {
    return (
      <div className="grid grid-nogutter justify-content-center align-items-center w-full">
        <img
          src="/images/pie-chart.png"
          style={{ width: '140px', height: '120px' }}
          alt="Pie"
        />
      </div>
    );
  }

  const labels = [];
  const values = [];
  const dispatch = useDispatch();
  if (cubeResponse && cubeResponse.length) {
    const copiedResponse = _.cloneDeep(cubeResponse);
    for (const item of copiedResponse) {
      for (const k of Object.keys(item)) {
        if (
          k?.includes('avg') ||
          k?.includes('sum') ||
          k?.includes('count') ||
          k?.includes('min') ||
          k?.includes('max')
        ) {
          values.push(item[k]);
          delete item[k];
          labels.push(
            Object.keys(item)
              ?.filter(f => f !== undefined)
              ?.map(record => {
                let formatter = null;
                Object.keys(item)?.forEach(i => {
                  if (annotation?.[i]?.meta?.format) {
                    formatter = eval(annotation[i].meta.format);
                  }
                });
                return formatter ? formatter(item[record]) : item[record];
              })
              ?.join(' | ')
          );
        }
      }
    }
  }

  const colors = React.useMemo(
    () => values.map(() => getRandomColor()),
    [values]
  );

  const data = {
    labels,
    datasets: [
      {
        data: values,
        backgroundColor: colors,
        borderWidth: 3,
        borderColor: '#ffffff',
        spacing: 2,
        borderRadius: 5,
        hoverOffset: 10,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: 0,
    },
    plugins: {
      legend: {
        display: ctx => {
          const width = ctx.chart.width;
          return width > 300;
        },
        position: 'right',
        align: 'center',
        labels: {
          usePointStyle: true,
          pointStyle: 'rectRounded',
          boxWidth: 16,
          boxHeight: 16,

          font: {
            size: 13,
            weight: '600',
          },
          color: '#333',
          generateLabels: chart => {
            const data = chart.data;
            if (data.datasets.length === 0) return [];

            return data?.labels?.map((label, index) => {
              const value = data?.datasets[0]?.data[index];
              const color = data?.datasets[0]?.backgroundColor[index];

              return {
                text: `${label}: ${value?.toLocaleString()}`,
                fillStyle: color,
                strokeStyle: color,
                lineWidth: 2,
                index,
              };
            });
          },
        },
      },
      tooltip: {
        enabled: true,
        backgroundColor: 'rgba(0,0,0,0.92)',
        titleFont: { size: 14 },
        bodyFont: { size: 13 },
        titleColor: 'white',
        bodyColor: 'white',
        borderColor: 'rgba(255,255,255,0.3)',
        borderWidth: 1,
        cornerRadius: 10,
        padding: 12,
        callbacks: {
          label: context => {
            const value = context.parsed;
            return `${context?.label}: ${value?.toLocaleString()}`;
          },
        },
      },
    },
    animation: {
      duration: 1400,
      easing: 'easeOutCubic',
    },
    onClick: (event, elements, chart) => {
      if (elements.length) {
        const { index } = elements[0];

        const clickedRawData = cubeResponse?.[index];

        dispatch(setChartClickResponse(clickedRawData));
        setTimeout(() => {
          const parentCard = chart.canvas.closest('.chartClick');
          parentCard.dataset.fromChart = 'true';
          if (parentCard) {
            parentCard.click();
          }
        }, 0);
      } else {
        dispatch(setChartClickResponse(null));
      }
    },
    interaction: {
      mode: 'nearest',
      intersect: true,
    },
  };

  if (!values?.length || values?.[0] == 0) {
    return (
      <div className="grid grid-nogutter justify-content-center align-items-center w-full">
        <img
          src="/images/pie-chart.png"
          style={{ width: '200px', height: '170px' }}
          alt="Pie"
        />
      </div>
    );
  }

  if (dashboardDetails && dashboardLogDetails) {
    storeDashboardAnalysis({
      ...dashboardLogDetails,
      ...dashboardDetails,
      executionTime: new Date().getTime() - dashboardDetails.start_time,
      end_time: new Date().getTime(),
    });
  }
  const chartRef = useRef(null);
  return (
    <div
      style={{
        width: '100%',
        position: 'relative',
        display: 'flex',
      }}
      onClick={e => {
        const chart = chartRef.current;
        if (!chart) {
          dispatch(setChartClickResponse(null));
          return;
        }

        const elements = chart.getElementsAtEventForMode(
          e.nativeEvent,
          'nearest',
          { intersect: true },
          true
        );

        if (elements.length) {
          e.stopPropagation();
          return;
        }

        dispatch(setChartClickResponse(null));
      }}
    >
      <Pie data={data} options={options} ref={chartRef} />
    </div>
  );
};

export default PieChartComponent;
